import { useCallback, useEffect, useState } from "react";
import rmsClient from "@/shared/api/clients/rmsClient";
import { ReportGenReport } from "../../../shared/interfaces/ReportGenReport";
import { ReportStatus } from "../../../shared/interfaces/ReportStatus";

export type CoReviewSegment = "pending" | "approved" | "returned";

/**
 * The "RMS Committed" tab is only relevant to stations that have an RMS.
 * No-RMS stations stop at CO APPROVED and never produce RMS COMMITTED
 * reports, so the presence of any such report tells us this station commits
 * to an RMS. (A station-metadata flag is the recorded backend ask; until
 * then this heuristic is the single source of the decision.)
 */
export function inferStationHasRms(committedTotal: number): boolean {
  return committedTotal > 0;
}

// The active CO-review backlog. Only genuine CO-track reports belong in the CO
// review queue — a legacy SIGNED report is a completed non-CO sign-off and is
// NOT CO-reviewable (the server gates CO review on the stored CO statuses), so
// it is intentionally excluded. These statuses are bounded in practice (a
// station only has so many reports awaiting review at once), so the queue
// fetches ALL of them, paginating to completion, rather than relying on a single
// capped page whose per-tab counts would silently undercount past the cap.
const WORKING_STATUSES = [
  ReportStatus.IN_CO_REVIEW,
  ReportStatus.CO_APPROVED,
  ReportStatus.CHANGES_REQUESTED,
];

// RMS COMMITTED is a permanent terminal status that grows without bound. It
// powers the "RMS Committed" tab (only RMS-enabled stations ever reach it), but
// fetching its full history would let it crowd out — and slow down — the working
// backlog. We surface an authoritative count for its tab badge and load only the
// most-recent page for display.
const COMMITTED_STATUS = ReportStatus.RMS_COMMITTED;

const PAGE_LIMIT = 100;

// Safety bound so a pathological data set can't spin the working-backlog loop
// forever. 20 pages == up to 2000 in-flight CO-track reports; far beyond any
// realistic review queue. If hit, `truncated` is surfaced rather than silently
// dropping reports.
const MAX_WORKING_PAGES = 20;

interface CoReviewReportsState {
  // The complete working backlog (IN CO REVIEW / CO APPROVED / CHANGES
  // REQUESTED) followed by the most-recent page of RMS COMMITTED reports.
  reports: ReportGenReport[];
  // Authoritative server-side count of RMS COMMITTED reports (may exceed the
  // number of committed reports present in `reports`).
  committedTotal: number;
  // True if the working backlog exceeded MAX_WORKING_PAGES and was not fully
  // loaded (a defensive cap, effectively unreachable in normal operation).
  truncated: boolean;
  isLoading: boolean;
  // True until the first fetch settles; refreshes keep the last counts.
  isInitialLoad: boolean;
  error: string | null;
  refresh: () => void;
}

export function useCoReviewReports(): CoReviewReportsState {
  const [reports, setReports] = useState<ReportGenReport[]>([]);
  const [committedTotal, setCommittedTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);

    // Page through every working-backlog report so the per-tab counts and lists
    // are complete. `total` (the server's full count for this status filter)
    // drives termination; sortBy=reportedByDate is monotonic with the table's
    // reportId ordering, so pages are disjoint and stable.
    const fetchAllWorking = async () => {
      const collected: ReportGenReport[] = [];
      let page = 0;
      let total = Number.POSITIVE_INFINITY;
      let didTruncate = false;

      while (collected.length < total) {
        if (page >= MAX_WORKING_PAGES) {
          didTruncate = true;
          break;
        }
        const res = await rmsClient.get("/reports", {
          params: {
            page,
            limit: PAGE_LIMIT,
            sortBy: "reportedByDate",
            sortOrder: "desc",
            status: WORKING_STATUSES.join(","),
          },
        });
        const batch: ReportGenReport[] = res.data?.reports ?? [];
        total = res.data?.total ?? collected.length + batch.length;
        collected.push(...batch);
        // Defensive: a short/empty page means there is nothing more to fetch.
        if (batch.length < PAGE_LIMIT) break;
        page += 1;
      }

      return { collected, didTruncate };
    };

    const fetchCommitted = async () => {
      const res = await rmsClient.get("/reports", {
        params: {
          page: 0,
          limit: PAGE_LIMIT,
          sortBy: "reportedByDate",
          sortOrder: "desc",
          status: COMMITTED_STATUS,
        },
      });
      return {
        committed: (res.data?.reports ?? []) as ReportGenReport[],
        total: (res.data?.total ?? 0) as number,
      };
    };

    // Settle independently: the working backlog drives the primary tabs and
    // must still render even if the secondary committed-tab fetch fails (and
    // vice versa). Promise.all would drop a successful working backlog whenever
    // fetchCommitted rejected.
    Promise.allSettled([fetchAllWorking(), fetchCommitted()])
      .then(([working, committed]) => {
        if (cancelled) return;

        if (working.status === "fulfilled") {
          const committedReports =
            committed.status === "fulfilled" ? committed.value.committed : [];
          setReports([...working.value.collected, ...committedReports]);
          setTruncated(working.value.didTruncate);
        }
        if (committed.status === "fulfilled") {
          setCommittedTotal(committed.value.total);
        }

        if (working.status === "rejected") {
          setError(
            working.reason?.response?.data?.error ||
              "Failed to load reports awaiting Commanding Officer review.",
          );
        } else if (committed.status === "rejected") {
          // Keep the working backlog displayed; only the committed list failed.
          setError(
            committed.reason?.response?.data?.error ||
              "Failed to load committed reports.",
          );
        } else {
          setError(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  return {
    reports,
    committedTotal,
    truncated,
    isLoading,
    isInitialLoad,
    error,
    refresh,
  };
}
